import {
  ICoversBySection, ISectionMaxOcc, IScheduleInterval
} from "app/app.types";
import MaxOccupancyService from "app/services/maxOccupancy/maxOccupancy.service";
import moment, { Moment, MomentInputObject } from 'moment';
import { IBooking } from "shared-types/index";

export const SLOT_INTERVAL = 1;

class BookingIntervalService {

  getCoversPerMinuteInterval(_bookingIntervals: { time: Date }[], bookings: IBooking[], sections: ISectionMaxOcc[]): IScheduleInterval[] {
    const bookingIntervals: IScheduleInterval[] = _bookingIntervals as IScheduleInterval[];
    bookingIntervals && bookingIntervals.forEach(interval => {

      let intervalCoversTotal = 0;
      let intervalCoversBySection: Map<string, ICoversBySection>;
      bookings?.forEach(b => {
        if (this.bookingIsValid(b)) {
          const bookingStartMins: number = this.getTimeInMins(b.time);
          const bookingEndMins: number = this.getTimeInMins(b.time) + this.getBookingDurationInMinutes(b);
          const intervalStartMins: number = this.getTimeInMins(interval.time);

          // if booking exists within interval
          if (bookingStartMins <= intervalStartMins && bookingEndMins > intervalStartMins) {

            intervalCoversTotal += b.people;

            if (!intervalCoversBySection) {
              intervalCoversBySection = new Map<string, ICoversBySection>();
            }

            const isMultipleSection = this.checkIfBookingOverMultipleSections(b);
            /**
             * Runs though each table and adds to the relevant section in `intervalCoversBySection`.
             * If tables are in different sections, splits the value between sections, filling them incrementally.
             */
            let tableCovers = b.people;
            b.tables.forEach(t => {
              const sectionCoversForInterval = intervalCoversBySection.get(t.sectionId) ? intervalCoversBySection.get(t.sectionId).sectionCovers : 0;
              // For Booking having people greater than the allowed capacity
              let coversToAddForSection = tableCovers;
              tableCovers = 0;

              coversToAddForSection = isMultipleSection ? t.capacity : coversToAddForSection;
              const covers = sectionCoversForInterval + coversToAddForSection; // Add number of booking pax if there is only 1 section
              intervalCoversBySection.set(t.sectionId, {
                'sectionCovers': covers, // total covers for that interval minute
                'sectionMarker': sections ? MaxOccupancyService.getTypeOfMarker(covers, sections.find(sec => sec._id === t.sectionId)) : '' // type of occupancy
              })
            });
          }
        }
      });

      interval.coversBySection = intervalCoversBySection && intervalCoversBySection.size > 0 ? intervalCoversBySection : null;
      interval.coversTotal = intervalCoversTotal;
    });

    return bookingIntervals;
  }


  /**
   *
   * @param bookingIntervals
   * @param section
   * @returns { intervals: any[]; type: string; }[] Returns bookingIntervalArray, which is the array of IScheduleInterval[]. The type here is the type of maxOccupancy
   *
   * this method loops on the booking intervals which are 1 minute slices and creates an array of Max Occupancy types array
   * as long as the types are same, it would keep adding in the sameTypeArray, and once a different max occupancy type is
   * encountered, it pushes all the sameTypeArray Values to bookingIntervalArray - which contains the array of arrays
   */

  getBookingIntervalBasedOnType(bookingIntervals: IScheduleInterval[], sectionId: string): { intervals: IScheduleInterval[]; type: string; }[] {
    let sameTypeArray: IScheduleInterval[] = [];
    const bookingIntervalArray: { intervals: IScheduleInterval[]; type: string; }[] = [];
    let intervalType = '';
    let oldIntervalType = '';
    let firstValueProcessed = false;

    bookingIntervals.forEach((interval: IScheduleInterval, index) => {
      if (!firstValueProcessed) { // to process the 1st value for comparison
        if (interval.coversBySection.get(sectionId)) {
          intervalType = interval.coversBySection.get(sectionId).sectionMarker;
          sameTypeArray.push(interval);
          firstValueProcessed = true;
        }
      } else {
        const oldInterval = bookingIntervals[index - 1];
        if (oldInterval?.coversBySection.get(sectionId)) {
          oldIntervalType = oldInterval.coversBySection.get(sectionId).sectionMarker;
          intervalType = interval.coversBySection.get(sectionId) ? interval.coversBySection.get(sectionId).sectionMarker : '';
          const isBreak = this.checkForBreakInTime(oldInterval, interval); // to check if there was a break in the booking times

          if (isBreak) {
            bookingIntervalArray.push({intervals: sameTypeArray, type: oldIntervalType}); // Push the existing values in the array and start a new loop
            sameTypeArray = [interval];
          } else {
            if (index !== bookingIntervals.length - 1) { // if not the last element
              if (intervalType === oldIntervalType) { // if the type of occupancy is the same continue pushing in the array

                let sameTypeItemIsActuallySameType = true; // assume true until proven otherwise
                if (sameTypeArray.length === 1) {
                  // when there is a gap between intervals with sectionMarkers, the sameTypeArray can be empty
                  const sectionItem = sameTypeArray[0].coversBySection.get(sectionId);
                  if (!sectionItem || sectionItem.sectionMarker !== intervalType) {
                    sameTypeItemIsActuallySameType = false;
                  }
                }

                if (sameTypeItemIsActuallySameType) {
                  sameTypeArray.push(interval); // Continue the same loop
                } else {
                  sameTypeArray = [oldInterval, interval]; // start a new loop
                }
              } else {
                // if type of occupancy is found to be different, put all the same types of intervals having same occupancy type in an array
                bookingIntervalArray.push({intervals: sameTypeArray, type: oldIntervalType}); // Push the existing values in the array and start a new loop
                sameTypeArray = [interval];
              }
            } else { // Last element
              if (sameTypeArray.length > 1) { // To handle the scenario where all the data is of one occupancy type - then loop till the last interval and if no change put all the data in the array
                sameTypeArray.push(interval);
                bookingIntervalArray.push({intervals: sameTypeArray, type: oldIntervalType}); // Push the existing values in the array and start a new loop
                sameTypeArray = [];
              }
            }
          }
        }
      }
    });

    return bookingIntervalArray;
  }

  checkIfBookingOverMultipleSections(booking: IBooking): boolean {
    const sectionIds = booking.tables.map(t => t.sectionId).filter(this.onlyUnique); // to find if the tables are spanned over multiple sections
    return sectionIds.length > 1;
  }
  // "getTimesBetweenStartAndEndTimes" moved to "date-utils-service" submodule
  private onlyUnique(value: any, index: number, self: any) {
    return self.indexOf(value) === index;
  }

  private bookingIsValid(booking: IBooking): boolean {
    return booking.status && booking.status.statusType !== 'cancelled' && (booking.tables && booking.tables.length > 0);
  }

  private getTimeInMins(t: Date): number {
    return (t.getHours() * 60) + t.getMinutes();
  }

  /**
   * `booking.duration` should be number (mins) but due to bug, could be string, so here we ensure we have a number
   */
  private getBookingDurationInMinutes(b: IBooking): number {
    if (typeof b.duration === 'number') {
      return b.duration;
    }
    return parseInt(b.duration, 10);
  }


  private checkForBreakInTime(oldInterval: IScheduleInterval, interval: IScheduleInterval): boolean {
    const nextIntervalTime = moment(oldInterval.time).add(SLOT_INTERVAL, 'minutes');
    const currentIntervalTime = moment(interval.time);
    return !nextIntervalTime.isSame(currentIntervalTime);
  }
}

const instance = new BookingIntervalService();
export default instance as BookingIntervalService;
